import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  PhoneInTalk as PhoneInTalkIcon,
  Router as RouterIcon,
  SettingsEthernet as SettingsEthernetIcon,
  Wifi as WifiIcon
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import {
  canManageTelephony,
  createTelephonyPairingCode,
  subscribeToTelephonyConnectors,
  toDate
} from '../../services/telephonyService';
import { detectTelephonyConnectorDownload } from '../../utils/telephonyConnectorPlatform';

const STORAGE_KEY = 'nextcab-telephony-guide-progress-v1';
const INITIAL_PROGRESS = {
  sameNetwork: false,
  fritzBoxReachable: false,
  callMonitorEnabled: false,
  installerDownloaded: false,
  installationFinished: false,
  testCallFinished: false
};

const readProgress = () => {
  try {
    return { ...INITIAL_PROGRESS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') };
  } catch (_) {
    return INITIAL_PROGRESS;
  }
};

const connectorIsOnline = (connector) => {
  if (!connector || connector.enabled === false) return false;
  const lastSeenAt = toDate(connector.lastSeenAt);
  return Boolean(lastSeenAt && Date.now() - lastSeenAt.getTime() <= 2 * 60 * 1000);
};

const TelephonyConnectorGuidePage = () => {
  const user = useSelector(selectCurrentUser);
  const mayManage = canManageTelephony(user);
  const connectorDownload = useMemo(() => detectTelephonyConnectorDownload(), []);
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(readProgress);
  const [displayName, setDisplayName] = useState('Dispositions-PC');
  const [pairing, setPairing] = useState(null);
  const [creatingCode, setCreatingCode] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  const latestConnector = connectors.find(connectorIsOnline) || connectors[0] || null;
  const online = connectors.some(connectorIsOnline);
  const setupCommand = pairing?.code
    ? `nextcab-telephony-connector setup --code ${pairing.code}`
    : '';

  const stepCompleted = [
    progress.sameNetwork && progress.fritzBoxReachable,
    progress.callMonitorEnabled,
    progress.installerDownloaded,
    progress.installationFinished || online,
    progress.testCallFinished
  ];
  const completedCount = stepCompleted.filter(Boolean).length;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [progress]);

  useEffect(() => {
    const unsubscribe = subscribeToTelephonyConnectors(user, (items) => {
      setConnectors(items);
      setStatusLoading(false);
    }, (snapshotError) => {
      setError(snapshotError.message || 'Connector-Status konnte nicht geladen werden.');
      setStatusLoading(false);
    });
    return unsubscribe;
  }, [user]);

  const setChecked = (key) => (event) => {
    setProgress(current => ({ ...current, [key]: event.target.checked }));
  };

  const nextStep = () => setActiveStep(current => Math.min(current + 1, 4));
  const previousStep = () => setActiveStep(current => Math.max(current - 1, 0));

  const createPairingCode = async () => {
    setCreatingCode(true);
    setError('');
    try {
      const result = await createTelephonyPairingCode(displayName);
      setPairing(result);
    } catch (callError) {
      setError(callError.message || 'Pairing-Code konnte nicht erzeugt werden.');
    } finally {
      setCreatingCode(false);
    }
  };

  const copyText = async (value, message) => {
    try {
      await navigator.clipboard.writeText(value);
      setSnackbar(message);
    } catch (_) {
      setError('Der Text konnte nicht in die Zwischenablage kopiert werden.');
    }
  };

  const resetGuide = () => {
    setProgress(INITIAL_PROGRESS);
    setPairing(null);
    setActiveStep(0);
    localStorage.removeItem(STORAGE_KEY);
  };

  const navigation = (canContinue = true) => (
    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
      <Button variant="contained" onClick={nextStep} disabled={!canContinue}>
        Weiter
      </Button>
      {activeStep > 0 && <Button onClick={previousStep}>Zurück</Button>}
    </Stack>
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <PageHeader
        title="Anruferkennung einrichten"
        description="FRITZ!Box vorbereiten, Connector installieren und Verbindung testen"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Einstellungen', path: '/einstellungen' },
          { label: 'Anruferkennung einrichten' }
        ]}
        secondaryButton={{
          label: 'Fortschritt zurücksetzen',
          onClick: resetGuide
        }}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
            >
              <Box>
                <Typography variant="h6">Einrichtungsfortschritt</Typography>
                <Typography variant="body2" color="text.secondary">
                  {completedCount} von 5 Schritten abgeschlossen
                </Typography>
              </Box>
              {connectorDownload ? (
                <Chip icon={<ComputerIcon />} label={`Erkannt: ${connectorDownload.system}`} color="primary" />
              ) : (
                <Chip label="Nicht unterstütztes System" color="warning" />
              )}
            </Stack>
            <LinearProgress variant="determinate" value={(completedCount / 5) * 100} />
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
          <Step completed={stepCompleted[0]}>
            <StepLabel onClick={() => setActiveStep(0)} sx={{ cursor: 'pointer' }}>
              FRITZ!Box und Netzwerk vorbereiten
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                <Alert icon={<WifiIcon />} severity="info">
                  Der Dispositionscomputer und die FRITZ!Box müssen sich im selben lokalen
                  Netzwerk befinden. Es darf keine Portfreigabe ins Internet eingerichtet werden.
                </Alert>
                <FormControlLabel
                  control={<Checkbox checked={progress.sameNetwork} onChange={setChecked('sameNetwork')} />}
                  label="Computer und FRITZ!Box befinden sich im selben Netzwerk"
                />
                <Box>
                  <Button
                    component="a"
                    href="http://fritz.box"
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    startIcon={<RouterIcon />}
                  >
                    FRITZ!Box im Browser öffnen
                  </Button>
                </Box>
                <FormControlLabel
                  control={<Checkbox checked={progress.fritzBoxReachable} onChange={setChecked('fritzBoxReachable')} />}
                  label="Die FRITZ!Box-Seite ist auf diesem Computer erreichbar"
                />
                {navigation(stepCompleted[0])}
              </Stack>
            </StepContent>
          </Step>

          <Step completed={stepCompleted[1]}>
            <StepLabel onClick={() => setActiveStep(1)} sx={{ cursor: 'pointer' }}>
              Call-Monitor aktivieren
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                <Alert icon={<PhoneInTalkIcon />} severity="warning">
                  Wählen Sie an einem Telefon, das mit der FRITZ!Box verbunden ist,
                  die Zeichenfolge <strong>#96*5*</strong>. Dadurch wird der lokale
                  Call-Monitor auf Port 1012 aktiviert.
                </Alert>
                <FormControlLabel
                  control={<Checkbox checked={progress.callMonitorEnabled} onChange={setChecked('callMonitorEnabled')} />}
                  label="#96*5* wurde gewählt und von der FRITZ!Box bestätigt"
                />
                <Typography variant="body2" color="text.secondary">
                  Ein FRITZ!Box-Benutzer oder Kennwort wird für die aktuelle
                  Echtzeit-Anruferkennung nicht benötigt.
                </Typography>
                {navigation(stepCompleted[1])}
              </Stack>
            </StepContent>
          </Step>

          <Step completed={stepCompleted[2]}>
            <StepLabel onClick={() => setActiveStep(2)} sx={{ cursor: 'pointer' }}>
              Passenden Connector herunterladen
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                {connectorDownload ? (
                  <>
                    <Alert severity="info">
                      Das erkannte Betriebssystem ist <strong>{connectorDownload.system}</strong>.
                      {' '}Der passende Installer wurde bereits ausgewählt.
                    </Alert>
                    <Box>
                      <Button
                        component="a"
                        href={connectorDownload.href}
                        download
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => setProgress(current => ({ ...current, installerDownloaded: true }))}
                      >
                        {connectorDownload.label}
                      </Button>
                    </Box>
                    <FormControlLabel
                      control={<Checkbox checked={progress.installerDownloaded} onChange={setChecked('installerDownloaded')} />}
                      label="Der Installer wurde heruntergeladen"
                    />
                    {connectorDownload.key === 'macos' && (
                      <Alert severity="warning">
                        Der aktuelle macOS-Testinstaller ist noch nicht von Apple signiert
                        und notarisiert. Falls macOS das Öffnen blockiert, versuchen Sie
                        zuerst, das Paket einmal zu öffnen. Öffnen Sie danach
                        <strong> Systemeinstellungen &gt; Datenschutz &amp; Sicherheit</strong>,
                        scrollen Sie zum Sicherheitshinweis und wählen Sie
                        <strong> Dennoch öffnen</strong>. Bestätigen Sie anschließend mit
                        Ihrem Mac-Passwort oder Touch ID.
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert severity="warning">
                    Öffnen Sie diese Anleitung auf dem Windows-, macOS- oder Linux-Computer,
                    der während der Disposition läuft.
                  </Alert>
                )}
                {navigation(stepCompleted[2])}
              </Stack>
            </StepContent>
          </Step>

          <Step completed={stepCompleted[3]}>
            <StepLabel onClick={() => setActiveStep(3)} sx={{ cursor: 'pointer' }}>
              Mit dem Portal verbinden
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                {connectorDownload?.key === 'macos' && (
                  <Alert severity="warning">
                    Sollte der Installer mit dem Hinweis blockiert werden, Apple könne ihn
                    nicht auf Schadsoftware prüfen, geben Sie ihn unter
                    <strong> Systemeinstellungen &gt; Datenschutz &amp; Sicherheit &gt; Dennoch öffnen</strong>
                    {' '}einmalig frei. Verwenden Sie diesen Übergangsweg nur für das direkt
                    aus dem NextCab-Portal heruntergeladene Paket.
                  </Alert>
                )}
                {!mayManage && (
                  <Alert severity="info">
                    Nur Administratoren können einen Connector koppeln. Bitten Sie einen
                    Administrator, diesen Schritt durchzuführen.
                  </Alert>
                )}
                <TextField
                  label="Name des Computers"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  disabled={!mayManage || Boolean(pairing)}
                  fullWidth
                />
                {!pairing ? (
                  <Button
                    variant="contained"
                    startIcon={creatingCode ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />}
                    onClick={createPairingCode}
                    disabled={!mayManage || creatingCode || !displayName.trim()}
                  >
                    Pairing-Code erzeugen
                  </Button>
                ) : (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Pairing-Code</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="h4" fontFamily="monospace" letterSpacing={3}>
                          {pairing.code}
                        </Typography>
                        <Button
                          startIcon={<ContentCopyIcon />}
                          onClick={() => copyText(pairing.code, 'Pairing-Code kopiert')}
                        >
                          Kopieren
                        </Button>
                      </Stack>
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Der Code ist zehn Minuten und nur einmal gültig.
                      </Alert>
                      {connectorDownload?.guidedSetup ? (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          {connectorDownload.key === 'macos'
                            ? 'Geben Sie diesen Code in die automatisch geöffnete Anwendung NextCab Telefonie-Connector ein. Die Anwendung richtet den Autostart ein und zeigt den Status von FRITZ!Box und Portal auf der letzten Seite.'
                            : 'Starten Sie den Windows-Installer und tragen Sie diesen Code sowie die FRITZ!Box-Adresse fritz.box ein. Der letzte Bildschirm zeigt das Ergebnis des Verbindungstests.'}
                        </Typography>
                      ) : (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Installieren Sie das Paket und führen Sie danach diesen Befehl aus:
                          </Typography>
                          <Box sx={{ p: 1.5, bgcolor: 'grey.900', color: 'grey.100', borderRadius: 1, overflowX: 'auto' }}>
                            <Typography component="code" fontFamily="monospace">
                              {setupCommand}
                            </Typography>
                          </Box>
                          <Button
                            sx={{ mt: 1 }}
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copyText(setupCommand, 'Einrichtungsbefehl kopiert')}
                          >
                            Befehl kopieren
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
                <FormControlLabel
                  control={<Checkbox checked={progress.installationFinished || online} onChange={setChecked('installationFinished')} />}
                  label="Die Installation wurde abgeschlossen"
                />
                {online && (
                  <Alert icon={<CheckCircleIcon />} severity="success">
                    Der Connector <strong>{latestConnector?.displayName || latestConnector?.hostname}</strong>
                    {' '}ist bereits online.
                  </Alert>
                )}
                {navigation(stepCompleted[3])}
              </Stack>
            </StepContent>
          </Step>

          <Step completed={stepCompleted[4]}>
            <StepLabel onClick={() => setActiveStep(4)} sx={{ cursor: 'pointer' }}>
              Verbindung und Testanruf prüfen
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <SettingsEthernetIcon color={online ? 'success' : 'action'} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography fontWeight="bold">Connector-Status</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {latestConnector?.displayName || latestConnector?.hostname || 'Noch kein Connector erkannt'}
                        </Typography>
                      </Box>
                      {statusLoading ? (
                        <CircularProgress size={22} />
                      ) : (
                        <Chip label={online ? 'Online' : 'Offline'} color={online ? 'success' : 'error'} />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
                <Typography variant="body1">
                  Rufen Sie die Geschäftsnummer von einem anderen Telefon an. Der Anruf
                  sollte innerhalb weniger Sekunden im Portal erscheinen.
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={progress.testCallFinished} onChange={setChecked('testCallFinished')} />}
                  label="Der Testanruf wurde im Portal angezeigt"
                />
                {stepCompleted[4] && (
                  <Alert icon={<CheckCircleIcon />} severity="success">
                    Die Anruferkennung ist eingerichtet. Bei einer bekannten Telefonnummer
                    kann das Portal nun eine passende Fahrtanfrage vorbereiten.
                  </Alert>
                )}
                {activeStep > 0 && <Button onClick={previousStep}>Zurück</Button>}
              </Stack>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Hilfe bei Problemen</Typography>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>FRITZ!Box oder Port 1012 nicht erreichbar</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              Prüfen Sie das lokale Netzwerk, trennen Sie testweise eine VPN-Verbindung
              und wählen Sie am FRITZ!Box-Telefon erneut #96*5*. Eine lokale Firewall darf
              die Verbindung zu Port 1012 nicht blockieren.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Windows meldet einen unbekannten Herausgeber</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              Der aktuelle Testinstaller ist noch nicht digital signiert. Wählen Sie bei
              SmartScreen "Weitere Informationen" und danach "Trotzdem ausführen".
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Connector bleibt offline</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              Der Dispositionscomputer muss eingeschaltet und mit Internet sowie dem
              FRITZ!Box-Netzwerk verbunden sein. Starten Sie den Installer bei Bedarf erneut
              und kontrollieren Sie den Verbindungstest auf der letzten Seite.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={2500}
        onClose={() => setSnackbar('')}
        message={snackbar}
      />
    </Container>
  );
};

export default TelephonyConnectorGuidePage;
